import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Folder, FolderPlus, FolderOpen, PencilSimple, Trash, ArrowLeft, DotsThreeVertical, Spinner, House,
} from "@phosphor-icons/react";

export const NewFolderButton = ({ onClick, testId }) => (
  <Button variant="outline" size="sm" onClick={onClick} data-testid={testId}
    className="rounded-full gap-1.5 h-8 px-3 text-xs text-navy border-slate-300 hover:border-navy/40">
    <FolderPlus size={15} weight="duotone" /> New Folder
  </Button>
);

export const FolderTiles = ({ folders, onOpen, kind, onDropItem }) => {
  const [over, setOver] = useState(null);
  if (!folders.length) return null;
  const accepts = (e) => e.dataTransfer.types.includes(`application/x-camino-${kind}`);
  return (
    <div className="mb-4 flex flex-wrap gap-2" data-testid={`${kind}-folder-tiles`}>
      {folders.map((f) => (
        <button key={f.id} onClick={() => onOpen(f.id)} data-testid={`folder-tile-${f.id}`}
          onDragOver={(e) => { if (accepts(e)) { e.preventDefault(); setOver(f.id); } }}
          onDragLeave={() => setOver((o) => (o === f.id ? null : o))}
          onDrop={(e) => {
            e.preventDefault();
            setOver(null);
            const id = e.dataTransfer.getData(`application/x-camino-${kind}`);
            if (id) onDropItem?.(id, f.id);
          }}
          className={`group flex items-center gap-2 rounded-lg border pl-3 pr-3.5 py-2 text-left transition-[transform,box-shadow,border-color,background-color] duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-navy/30 ${
            over === f.id ? "border-action bg-action/10 scale-105 shadow-md" : "border-slate-200 bg-white"}`}>
          <Folder size={20} weight="duotone" className="text-action shrink-0 group-hover:hidden" />
          <FolderOpen size={20} weight="duotone" className="text-action shrink-0 hidden group-hover:block" />
          <span className="text-sm font-semibold text-navy truncate max-w-[180px]">{f.name}</span>
          <span className="text-[11px] font-semibold text-slate-400 bg-slate-100 rounded-full px-1.5">{f.item_count}</span>
        </button>
      ))}
    </div>
  );
};

export const FolderBar = ({ folder, label, onBack, onRename, onDelete }) => {
  const empty = folder.item_count === 0;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`folder-bar-${folder.id}`}>
      <button onClick={onBack} data-testid="folder-back-btn" className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-navy transition-colors">
        <ArrowLeft size={14} weight="bold" /> All {label}
      </button>
      <span className="text-slate-300">/</span>
      <FolderOpen size={18} weight="duotone" className="text-action" />
      <span className="text-sm font-semibold text-navy truncate min-w-0 flex-1" data-testid="open-folder-name">{folder.name}</span>
      <Button variant="ghost" size="sm" onClick={onRename} data-testid="rename-folder-btn" className="h-8 gap-1 text-xs text-navy">
        <PencilSimple size={14} /> Rename
      </Button>
      <Button variant="ghost" size="sm" onClick={onDelete} disabled={!empty} data-testid="delete-folder-btn"
        title={empty ? "Delete this folder" : "Move or delete everything inside first"}
        className="h-8 gap-1 text-xs text-destructive disabled:text-slate-300">
        <Trash size={14} /> Delete folder
      </Button>
      {!empty && <span className="w-full text-[11px] text-slate-400">To delete this folder, move or delete everything inside it first.</span>}
    </div>
  );
};

export const ItemMenu = ({ testId, inFolder, hasFolders, onMove, onRemoveFromFolder, onEdit, onDelete, deleteLabel }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} data-testid={testId}
        className="shrink-0 rounded-md p-1 text-slate-400 hover:text-navy hover:bg-slate-100 transition-colors" title="More options">
        <DotsThreeVertical size={18} weight="bold" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()} className="w-48">
      {onEdit && (
        <DropdownMenuItem onSelect={onEdit} data-testid={`${testId}-edit`}><PencilSimple size={15} className="mr-2" /> Edit template</DropdownMenuItem>
      )}
      <DropdownMenuItem onSelect={onMove} data-testid={`${testId}-move`}>
        <Folder size={15} className="mr-2" /> {inFolder ? "Move to another folder" : hasFolders ? "Add to folder" : "Add to new folder"}
      </DropdownMenuItem>
      {inFolder && (
        <DropdownMenuItem onSelect={onRemoveFromFolder} data-testid={`${testId}-remove-folder`}><House size={15} className="mr-2" /> Remove from folder</DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={onDelete} data-testid={`${testId}-delete`} className="text-destructive focus:text-destructive">
        <Trash size={15} className="mr-2" /> {deleteLabel}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export const FolderNameDialog = ({ open, title, initial = "", onClose, onSave }) => {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setName(initial); }, [open, initial]);
  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await onSave(name.trim()); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="folder-name-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
            <FolderPlus size={22} weight="duotone" className="text-action" /> {title}
          </DialogTitle>
        </DialogHeader>
        <Input autoFocus value={name} maxLength={60} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()} placeholder="Folder name" className="h-11" data-testid="folder-name-input" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!name.trim() || busy} className="rounded-full bg-navy hover:bg-navy/90 gap-2" data-testid="folder-name-save-btn">
            {busy && <Spinner size={16} className="animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MoveDialog = ({ target, folders, onClose, onMove, onNewFolder }) => (
  <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="move-folder-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
          <Folder size={22} weight="duotone" className="text-action" /> Move to folder
        </DialogTitle>
      </DialogHeader>
      <p className="text-sm text-slate-500 -mt-2 truncate">{target?.label}</p>
      <div className="grid gap-1.5 max-h-72 overflow-y-auto">
        {folders.map((f) => {
          const current = target?.folder_id === f.id;
          return (
            <button key={f.id} disabled={current} onClick={() => onMove(f.id)} data-testid={`move-to-${f.id}`}
              className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 text-left text-sm font-medium text-navy transition-colors hover:border-navy/40 hover:bg-slate-50 disabled:opacity-50">
              <Folder size={18} weight="duotone" className="text-action" /> <span className="truncate flex-1">{f.name}</span>
              {current && <span className="text-[11px] text-slate-400">current</span>}
            </button>
          );
        })}
        {!folders.length && <p className="text-sm text-slate-400">No folders yet.</p>}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onNewFolder} className="rounded-full gap-1.5 text-navy" data-testid="move-new-folder-btn">
          <FolderPlus size={15} /> New folder
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
